import React, { useState } from 'react'
import { learningService, type FrenchActionPayload, type FrenchActionResult } from '../../../services/learning'
import type { FrenchPracticeKind, FrenchPracticeStructuredResult, FrenchRubricCriterion } from '../../../types/learning'

// Quebec Secondary levels. Default to Secondary 2 / intermediate per product spec.
const LEVEL_OPTIONS = [
  { value: 'secondary_1', label: 'Secondary 1 (beginner)' },
  { value: 'secondary_2', label: 'Secondary 2 (intermediate)' },
  { value: 'secondary_3', label: 'Secondary 3 (advanced)' },
]

type RunState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'setup_required'; message: string }
  | { status: 'error'; message: string }
  | { status: 'done'; result: FrenchPracticeStructuredResult | null; rawText: string | null }

const asTextList = (value: unknown): string[] => {
  if (value == null) return []
  if (Array.isArray(value)) return value.flatMap(asTextList)
  if (typeof value === 'object') {
    // Render objects like {term, definition} or {question, answer} as readable pairs
    // instead of dumping field names; otherwise flatten nested values.
    const obj = value as Record<string, unknown>
    const term = obj.term ?? obj.question ?? obj.original ?? obj.label
    const detail = obj.definition ?? obj.answer ?? obj.correction ?? obj.explanation ?? obj.status
    if (term != null && detail != null) {
      const t = String(term).trim()
      const d = String(detail).trim()
      return t || d ? [[t, d].filter(Boolean).join(' — ')] : []
    }
    return Object.values(obj).flatMap(asTextList)
  }
  const text = String(value).trim()
  return text ? [text] : []
}

const asText = (value: unknown): string => asTextList(value).join(' ').trim()

// First non-empty value across a list of likely aliases. AI payloads vary, so each
// learner-facing field accepts several names (vocabulary / vocabularyFocus / …).
const pick = (result: FrenchPracticeStructuredResult, ...keys: string[]): unknown => {
  for (const key of keys) {
    const value = result[key]
    if (value == null) continue
    if (Array.isArray(value) && value.length === 0) continue
    if (typeof value === 'string' && !value.trim()) continue
    return value
  }
  return undefined
}

// Normalise rubric/criteria into labelled checklist entries. Accepts strings, arrays of
// strings, arrays of {label,status}, or a {label: status} map.
const asCriteria = (value: unknown): FrenchRubricCriterion[] => {
  if (value == null) return []
  if (Array.isArray(value)) return value.flatMap(asCriteria)
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (obj.label != null || obj.criterion != null || obj.name != null) {
      const label = String(obj.label ?? obj.criterion ?? obj.name ?? '').trim()
      const status = obj.status != null ? String(obj.status).trim() : undefined
      return label ? [{ label, status }] : []
    }
    return Object.entries(obj).flatMap(([label, status]) => {
      const text = label.trim()
      return text ? [{ label: text, status: typeof status === 'string' ? status.trim() : undefined }] : []
    })
  }
  const text = String(value).trim()
  return text ? [{ label: text }] : []
}

// Pull the coach feedback summary/strengths/focus from either the nested coachFeedback
// object (new payloads) or flattened top-level fields (legacy payloads).
const coachFeedbackOf = (result: FrenchPracticeStructuredResult) => {
  const nested = (result.coachFeedback && typeof result.coachFeedback === 'object' && !Array.isArray(result.coachFeedback))
    ? (result.coachFeedback as Record<string, unknown>)
    : {}
  const summary = asText(pick({ ...nested } as FrenchPracticeStructuredResult, 'summary', 'overview')) || asText(result.summary)
  const strengths = asTextList(nested.strengths ?? result.strengths)
  const focusAreas = asTextList(nested.needsImprovement ?? nested.focusAreas ?? result.needsImprovement ?? result.weaknesses ?? result.focusAreas)
  return { summary, strengths, focusAreas }
}

// Friendly field renderer: a labelled block when there is a single value, a list when
// the field is a collection. Keeps the teach/explain framing (never "homework done").
const Field: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  const list = asTextList(value)
  if (!list.length) return null
  if (list.length === 1) {
    return (
      <div className="stack-sm">
        <strong>{label}</strong>
        <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{list[0]}</p>
      </div>
    )
  }
  return (
    <div className="stack-sm">
      <strong>{label}</strong>
      <ul>{list.map((item, index) => <li key={`${label}-${index}`}>{item}</li>)}</ul>
    </div>
  )
}

// A readable text card (used for dictée text / passage) — visually distinct from the
// labelled feedback fields so the learner reads it as the material, not as feedback.
const TextCard: React.FC<{ label: string; value: unknown; testId?: string }> = ({ label, value, testId }) => {
  const text = asText(value)
  if (!text) return null
  return (
    <div className="stack-sm">
      <strong>{label}</strong>
      <p className="card text-prewrap" data-testid={testId} style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{text}</p>
    </div>
  )
}

const Chips: React.FC<{ label: string; value: unknown; testId?: string }> = ({ label, value, testId }) => {
  const list = asTextList(value)
  if (!list.length) return null
  return (
    <div className="stack-sm">
      <strong>{label}</strong>
      <div className="admin-chip-list" data-testid={testId}>
        {list.map((item, index) => <span key={`${label}-${index}`} className="admin-chip">{item}</span>)}
      </div>
    </div>
  )
}

const criterionStatusLabel = (status?: string) => {
  const normalized = (status || '').toLowerCase()
  if (normalized === 'good' || normalized === 'ok' || normalized === 'strong') return '✓'
  if (normalized === 'focus' || normalized === 'weak') return '!'
  return '•'
}

const RubricChecklist: React.FC<{ label: string; value: unknown; testId?: string }> = ({ label, value, testId }) => {
  const criteria = asCriteria(value)
  if (!criteria.length) return null
  return (
    <div className="stack-sm">
      <strong>{label}</strong>
      <div className="admin-chip-list" data-testid={testId}>
        {criteria.map((criterion, index) => (
          <span key={`${label}-${index}`} className="admin-chip" title={criterion.status || undefined}>
            {criterionStatusLabel(criterion.status)} {criterion.label}
          </span>
        ))}
      </div>
    </div>
  )
}

const Callout: React.FC<{ label: string; value: unknown; testId?: string }> = ({ label, value, testId }) => {
  const text = asText(value)
  if (!text) return null
  return (
    <div className="helper" data-testid={testId} style={{ whiteSpace: 'pre-wrap' }}>
      <strong>{label}:</strong> {text}
    </div>
  )
}

const interpretResult = (response: FrenchActionResult): RunState => {
  if (response.setupRequired) {
    return { status: 'setup_required', message: response.message || 'The French School Coach is not configured yet. Ask an administrator to set up the AI provider.' }
  }
  if (!response.success) {
    return { status: 'error', message: response.message || 'The coach could not respond. Please try again.' }
  }
  const result = response.result
  if (result && typeof result === 'object') {
    return { status: 'done', result: result as FrenchPracticeStructuredResult, rawText: null }
  }
  // Unexpected shape — surface the raw text so the learner still gets feedback.
  return { status: 'done', result: null, rawText: typeof result === 'string' ? result : JSON.stringify(result ?? '', null, 2) }
}

// Shared coach feedback block (summary callout + strengths + focus areas). Rendered
// across all three practice kinds so feedback always reads the same way.
const CoachFeedbackBlock: React.FC<{ result: FrenchPracticeStructuredResult }> = ({ result }) => {
  const { summary, strengths, focusAreas } = coachFeedbackOf(result)
  if (!summary && !strengths.length && !focusAreas.length) return null
  return (
    <>
      {summary && (
        <div className="stack-sm" data-testid="french-practice-coach-summary">
          <strong>Coach feedback</strong>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{summary}</p>
        </div>
      )}
      {strengths.length > 0 && (
        <div className="stack-sm" data-testid="french-practice-strengths">
          <strong>Strengths</strong>
          <ul>{strengths.map((item, index) => <li key={`strength-${index}`}>{item}</li>)}</ul>
        </div>
      )}
      {focusAreas.length > 0 && (
        <div className="stack-sm" data-testid="french-practice-focus">
          <strong>Focus areas</strong>
          <ul>{focusAreas.map((item, index) => <li key={`focus-${index}`}>{item}</li>)}</ul>
        </div>
      )}
    </>
  )
}

const ResultBlock: React.FC<{ kind: FrenchPracticeKind; state: Extract<RunState, { status: 'done' }>; learnerText?: string }> = ({ kind, state, learnerText }) => {
  if (!state.result) {
    // Final fallback only: clearly labelled raw debug section, never silent JSON.
    return (
      <div className="card stack" data-testid="french-practice-raw">
        <strong>Raw result</strong>
        <p className="helper">The coach replied, but the feedback could not be formatted into cards. Showing the raw response.</p>
        <pre className="text-muted" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{state.rawText || 'The coach replied, but the feedback could not be formatted.'}</pre>
      </div>
    )
  }
  const result = state.result
  const title = asText(pick(result, 'title'))
  const nextExercise = pick(result, 'suggestedNextExercise', 'nextExercise', 'practicePlan')
  const grammar = pick(result, 'grammarFocus', 'grammar', 'grammarNotes')
  const rubric = pick(result, 'criteria', 'rubric', 'correctionRubric')
  return (
    <div className="card stack" data-testid="french-practice-result">
      <div className="status-pill status-pill--approved" data-testid="french-practice-saved">Practice recorded</div>
      {title && <h4 style={{ margin: 0 }} data-testid="french-practice-title">{title}</h4>}

      {kind === 'writing' && (
        <>
          {learnerText?.trim() && <TextCard label="Your text" value={learnerText} testId="french-practice-original" />}
          <TextCard label="Corrected version" value={pick(result, 'correctedVersion')} testId="french-practice-corrected" />
          <CoachFeedbackBlock result={result} />
          <Field label="Mistakes to learn from" value={pick(result, 'mistakeList', 'mistakes', 'commonMistakes')} />
          <Field label="Grammar explanation" value={pick(result, 'grammarExplanation', 'grammar', 'grammarNotes')} />
          <Field label="Rewrite practice" value={pick(result, 'rewritePractice')} />
          <Field label="Score" value={pick(result, 'score')} />
          <Callout label="Suggested next exercise" value={nextExercise} testId="french-practice-next" />
        </>
      )}

      {kind === 'dictee' && (
        <>
          <TextCard label="Dictée text" value={pick(result, 'dicteeText')} testId="french-practice-dictee-text" />
          <Chips label="Vocabulary focus" value={pick(result, 'vocabularyFocus', 'vocabulary', 'vocabularyList')} testId="french-practice-vocabulary" />
          <CoachFeedbackBlock result={result} />
          <Field label="Grammar focus" value={grammar} />
          <RubricChecklist label="Rubric / criteria" value={rubric} testId="french-practice-rubric" />
          <Field label="Common mistakes to watch for" value={pick(result, 'commonMistakesToWatchFor', 'commonMistakes', 'mistakes')} />
          <Callout label="Support note" value={pick(result, 'supportNote')} testId="french-practice-support" />
          <Callout label="Suggested next exercise" value={nextExercise} testId="french-practice-next" />
        </>
      )}

      {kind === 'reading' && (
        <>
          <TextCard label="Passage" value={pick(result, 'passage')} testId="french-practice-passage-text" />
          <Field label="Simplified explanation" value={pick(result, 'simplifiedExplanation')} />
          <Chips label="Vocabulary" value={pick(result, 'vocabulary', 'vocabularyList', 'vocabularyFocus')} testId="french-practice-vocabulary" />
          <CoachFeedbackBlock result={result} />
          <Field label="Comprehension questions" value={pick(result, 'comprehensionQuestions', 'questions')} />
          <Field label="Answer key" value={pick(result, 'answerKey')} />
          <RubricChecklist label="Rubric / feedback" value={pick(result, 'rubric', 'feedbackRubric', 'criteria')} testId="french-practice-rubric" />
          <Callout label="Suggested next exercise" value={nextExercise} testId="french-practice-next" />
        </>
      )}

      <p className="helper">This is correction, explanation, and practice — not a finished assignment. Ask a teacher or summer-school support if you stay stuck.</p>
    </div>
  )
}

interface FrenchPracticeProps {
  // Called after a successful, recorded practice so the parent can regenerate and
  // re-render recommendation cards with the new practice signals.
  onPracticeRecorded: () => void | Promise<void>
}

export const FrenchPractice: React.FC<FrenchPracticeProps> = ({ onPracticeRecorded }) => {
  const [activeKind, setActiveKind] = useState<FrenchPracticeKind | null>(null)
  const [level, setLevel] = useState('secondary_2')
  const [languagePreference, setLanguagePreference] = useState<'fr' | 'en'>('fr')
  const [studentParagraph, setStudentParagraph] = useState('')
  const [assignmentGoal, setAssignmentGoal] = useState('')
  const [theme, setTheme] = useState('')
  const [passage, setPassage] = useState('')
  const [run, setRun] = useState<RunState>({ status: 'idle' })

  const openKind = (kind: FrenchPracticeKind) => {
    setActiveKind(kind)
    setRun({ status: 'idle' })
  }

  const submit = async (kind: FrenchPracticeKind) => {
    setRun({ status: 'loading' })
    const base: FrenchActionPayload = {
      learnerLevel: level,
      languagePreference,
      recordActivity: true,
    }
    try {
      let response: FrenchActionResult
      if (kind === 'writing') {
        response = (await learningService.frenchWritingCorrection({ ...base, studentParagraph: studentParagraph.trim() || null, assignmentGoal: assignmentGoal.trim() || null })).data
      } else if (kind === 'dictee') {
        response = (await learningService.frenchDictee({ ...base, theme: theme.trim() || null })).data
      } else {
        response = (await learningService.frenchReadingComprehension({
          ...base,
          passage: passage.trim() || null,
          generatedPassageRequest: passage.trim() ? null : (theme.trim() || 'a short age-appropriate passage'),
        })).data
      }
      const next = interpretResult(response)
      setRun(next)
      if (next.status === 'done') {
        void onPracticeRecorded()
      }
    } catch (err) {
      const message = (err as any)?.response?.data?.message
      setRun({ status: 'error', message: typeof message === 'string' && message.trim() ? message : 'We could not reach the French School Coach. Please retry.' })
    }
  }

  return (
    <div className="stack" data-testid="learning-french-practice">
      <h3>French Practice</h3>
      <p className="text-muted">Submit French practice and the School Coach will correct, explain, and suggest next steps. It teaches — it never just finishes your homework.</p>

      <div className="card-grid">
        <button type="button" className="card stack" data-testid="french-practice-open-writing" onClick={() => openKind('writing')}>
          <strong>Submit writing</strong>
          <span className="text-muted">Get gentle corrections, grammar help, and a rewrite to practice.</span>
        </button>
        <button type="button" className="card stack" data-testid="french-practice-open-dictee" onClick={() => openKind('dictee')}>
          <strong>Practice dictée</strong>
          <span className="text-muted">Generate a dictée with a rubric and the mistakes to watch for.</span>
        </button>
        <button type="button" className="card stack" data-testid="french-practice-open-reading" onClick={() => openKind('reading')}>
          <strong>Reading comprehension</strong>
          <span className="text-muted">Paste or request a passage, then learn vocabulary and questions.</span>
        </button>
      </div>

      {activeKind && (
        <form
          className="card stack"
          data-testid="french-practice-form"
          onSubmit={(event) => { event.preventDefault(); void submit(activeKind) }}
        >
          <div className="button-row" style={{ flexWrap: 'wrap' }}>
            <label>
              Level
              <select className="input" value={level} onChange={(event) => setLevel(event.target.value)} data-testid="french-practice-level">
                {LEVEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label>
              Language to explain in
              <select className="input" value={languagePreference} onChange={(event) => setLanguagePreference(event.target.value as 'fr' | 'en')}>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </label>
          </div>

          {activeKind === 'writing' && (
            <>
              <label>
                Your paragraph
                <textarea className="input" rows={5} value={studentParagraph} onChange={(event) => setStudentParagraph(event.target.value)} data-testid="french-practice-paragraph" placeholder="Écris ton paragraphe ici…" />
              </label>
              <label>
                Goal or prompt (optional)
                <input className="input" value={assignmentGoal} onChange={(event) => setAssignmentGoal(event.target.value)} placeholder="e.g. describe my weekend in past tense" />
              </label>
            </>
          )}

          {activeKind === 'dictee' && (
            <label>
              Theme (optional)
              <input className="input" value={theme} onChange={(event) => setTheme(event.target.value)} data-testid="french-practice-theme" placeholder="e.g. everyday life, school, sports" />
            </label>
          )}

          {activeKind === 'reading' && (
            <>
              <label>
                Passage (paste one, or leave blank to generate)
                <textarea className="input" rows={5} value={passage} onChange={(event) => setPassage(event.target.value)} data-testid="french-practice-passage" placeholder="Colle un texte ou laisse vide pour en générer un…" />
              </label>
              <label>
                Topic for a generated passage (optional)
                <input className="input" value={theme} onChange={(event) => setTheme(event.target.value)} placeholder="e.g. winter in Quebec" />
              </label>
            </>
          )}

          <div className="button-row">
            <button type="submit" className="btn btn-primary" disabled={run.status === 'loading'} data-testid="french-practice-submit">
              {run.status === 'loading' ? 'Coach is thinking…' : activeKind === 'writing' ? 'Submit for correction' : activeKind === 'dictee' ? 'Generate dictée' : 'Get comprehension help'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setActiveKind(null); setRun({ status: 'idle' }) }}>Close</button>
          </div>

          {run.status === 'loading' && <p className="text-muted" data-testid="french-practice-loading">Generating supportive feedback…</p>}
          {run.status === 'setup_required' && <div className="admin-error-banner" role="status" data-testid="french-practice-setup">{run.message}</div>}
          {run.status === 'error' && <div className="admin-error-banner" role="alert" data-testid="french-practice-error">{run.message}</div>}
          {run.status === 'done' && <ResultBlock kind={activeKind} state={run} learnerText={activeKind === 'writing' ? studentParagraph : undefined} />}
        </form>
      )}
    </div>
  )
}
